import React, { useEffect, useState } from 'react'
import MyTeamPage from 'components/templates/navigationPageTemplates/MyTeamPage'
import { Stack, Typography, Tab } from '@mui/material'
import RowStack from 'components/atoms/container/RowStack'
import { getUserConnections } from 'services/userServices'
import OverlayLoading from 'components/atoms/OverlayLoading'
import AddConnectionButton from 'components/molecules/myTeam/AddConnectionButton'
import BookOneOnOne from 'components/organisms/myTeam/BookOneOnOne'
import { useTranslation } from 'react-i18next'
import { FEATURE_KEYS, USER_CALENDAR_EVENTS_URL } from 'services/constants'
import ImportOneOnOne from 'components/organisms/myTeam/ImportOneOnOne'
import AddNoteTaker from 'components/organisms/myTeam/AddNoteTaker'
import UpcomingOneOnOnes from 'components/molecules/myTeam/UpcomingOneOnOnes'
import TeamView from 'components/molecules/myTeam/TeamView'
import { getRequest } from 'utils/api'
import InCardStack from 'components/atoms/container/InCardStack'
import { InfoCardIcon } from 'components/atoms/Icons'
import addConnectionIcon from 'assets/images/addConnection.svg'
import importIcon from 'assets/images/import.svg'
import addMeetingIcon from 'assets/images/addMeeting.svg'
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined'
import ExitToAppOutlinedIcon from '@mui/icons-material/ExitToAppOutlined'
import { EmptyMeetingCard } from 'components/atoms/InfoCard'
import { isAllowedFeature } from 'utils/permissionCheck'
import UpcomingMeetingNote from 'components/molecules/myTeam/UpcomingMeetingNote'
import ConnectCalendarDialog from 'components/molecules/home/popups/ConnectCalendarDialog'
import moment from 'moment'
import ToggleTabs from 'components/atoms/button/ToggleTabs'
import PastOneOnOnes from 'components/molecules/myTeam/PastOneOnOnes'
import { updatePageState } from 'utils/windowHistoryHelper'

const MeetingsPage = () => {
  const { t, i18n } = useTranslation()
  moment.locale(i18n.resolvedLanguage)
  const [userConnections, setUserConnections] = useState([])
  const [otherUserConnections, setOtherUserConnections] = useState([])
  const [events, setEvents] = useState({})
  const [pagination, setPagination] = useState({ next: 1, total: 0 })
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const hasLicense = isAllowedFeature(FEATURE_KEYS.ONE_ON_ONE_PAID)
  const [activeTab, setActiveTab] = useState(
    window.history?.state?.activeTab || 0
  )

  const minDateTime = moment.utc().toISOString()
  const pageLimit = 10

  const fetchConnData = async () => {
    setLoading(true)
    const res = await getUserConnections()
    if (res.length) {
      setUserConnections(
        res
          .filter(u => u.relationship)
          .sort((a, b) => {
            if (!a.user?.firstName) return 1
            if (!b.user?.firstName) return -1
            return a.user.firstName > b.user.firstName ? 1 : -1
          })
      )
      setOtherUserConnections(res.filter(u => !u.relationship))
    }
    setLoading(false)
  }

  const paginateCalendarEvents = async (
    next = pagination.next,
    prevEvents = events
  ) => {
    if (next) {
      const res = await getRequest(USER_CALENDAR_EVENTS_URL, {
        limit: pageLimit,
        page: next,
        minDateTime
      })

      if (res?.status === 200) {
        const newEvents = res.data.data.data.reduce((acc, event) => {
          const startTime = moment(event.startTime).format('YYYY-MM-DD')
          if (!acc[startTime]) {
            acc[startTime] = []
          }
          acc[startTime].push(event)
          return acc
        }, {})
        setEvents({ ...prevEvents, ...newEvents })
        setPagination(res.data.data.pagination)
      }
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([fetchConnData(), paginateCalendarEvents()])
      setInitialLoading(false)
    }
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refreshData = () => {
    fetchConnData()
    paginateCalendarEvents(1, {})
  }

  return (
    <MyTeamPage>
      {!initialLoading && (
        <>
          <Stack width='100%' spacing={10}>
            <RowStack
              width='100%'
              sx={{
                justifyContent: 'space-between'
              }}>
              <RowStack spacing={8} width='auto'>
                <Typography variant='h1'>{t('myTeam.meetings')}</Typography>
                <ToggleTabs
                  activeTab={activeTab}
                  setActiveTab={active => {
                    setActiveTab(active)
                    updatePageState({ activeTab: active })
                  }}>
                  <Tab value={0} label={t('myTeam.upcoming1on1')} />
                  <Tab value={1} label={t('myTeam.past1on1')} />
                </ToggleTabs>
              </RowStack>

              {events && Object.keys(events).length > 0 && (
                <UpcomingMeetingNote />
              )}

              <RowStack
                sx={{ justifyContent: 'flex-end', width: 'auto' }}
                spacing={4}>
                <AddNoteTaker events={events} sucessCallback={refreshData} />
                <BookOneOnOne
                  sucessCallback={refreshData}
                  buttonLabel={t('myTeam.bookOneOnOne')}
                  buttonProps={{
                    variant: 'outlined',
                    color: 'primary',
                    size: 'small',
                    startIcon: <TodayOutlinedIcon />
                  }}
                />
                {hasLicense && (
                  <ImportOneOnOne
                    sucessCallback={refreshData}
                    buttonProps={{
                      size: 'small',
                      startIcon: <ExitToAppOutlinedIcon />
                    }}
                  />
                )}
              </RowStack>
            </RowStack>
            {activeTab === 0 ? (
              <>
                <InCardStack spacing={6} alignItems={'flex-start'}>
                  {events && Object.keys(events).length > 0 ? (
                    <UpcomingOneOnOnes
                      events={events}
                      paginateEvents={paginateCalendarEvents}
                    />
                  ) : (
                    <>
                      <Typography>
                        {t('myTeam.haveNoUpcomingMeetings')}
                      </Typography>
                      <RowStack spacing={8}>
                        {hasLicense && (
                          <EmptyMeetingCard
                            icon={<InfoCardIcon src={importIcon} size={64} />}
                            title={t('calendarEvents.import1on1')}
                            content={t('calendarEvents.importExisting1on1')}>
                            <ImportOneOnOne sucessCallback={refreshData} />
                          </EmptyMeetingCard>
                        )}

                        <EmptyMeetingCard
                          icon={<InfoCardIcon src={addMeetingIcon} size={64} />}
                          title={t('myTeam.book1on1Meeting')}
                          content={t('myTeam.setUpRecurring1on1s')}>
                          <BookOneOnOne
                            sucessCallback={refreshData}
                            buttonLabel={t('myTeam.bookOneOnOne')}
                          />
                        </EmptyMeetingCard>
                      </RowStack>
                    </>
                  )}
                </InCardStack>
                <InCardStack spacing={6} alignItems={'flex-start'}>
                  <Typography variant='h2'>
                    {t('myTeam.connections')}
                  </Typography>
                  {userConnections?.length > 0 ? (
                    <TeamView
                      userConnections={userConnections}
                      addConnectionButton={
                        <AddConnectionButton
                          users={otherUserConnections}
                          sucessCallback={refreshData}
                          buttonProps={{
                            size: 'small',
                            color: 'primary',
                            variant: 'outlined'
                          }}
                        />
                      }
                    />
                  ) : (
                    <>
                      <Typography variant='body1'>
                        {t('myTeam.haveNoConnections')}
                      </Typography>

                      <EmptyMeetingCard
                        icon={
                          <InfoCardIcon src={addConnectionIcon} size={64} />
                        }
                        title={t('myTeam.addAConnection')}
                        content={t('myTeam.addYourConnectionsToBeginBooking')}>
                        <AddConnectionButton
                          users={otherUserConnections}
                          sucessCallback={refreshData}
                        />
                      </EmptyMeetingCard>
                    </>
                  )}{' '}
                </InCardStack>
              </>
            ) : (
              <PastOneOnOnes />
            )}
          </Stack>

          <ConnectCalendarDialog />
        </>
      )}
      <OverlayLoading open={loading} />
    </MyTeamPage>
  )
}

export default MeetingsPage
